import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, retry, throwError, timer } from 'rxjs';
import { RetryProgressService } from '../../services/retry-progress.service';

const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 1000;
const MAX_DELAY_MS = 5000;

/**
 * Retry HTTP requests with exponential backoff on network/server errors
 * Auto-resets retry progress UI after request completes
 *
 * Retryable errors: status 0 (network), 408 (timeout), 5xx (server)
 * Non-retryable: 4xx (client errors)
 */
export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const retryProgressService = inject(RetryProgressService);

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        const delay = Math.min(
          INITIAL_DELAY_MS * Math.pow(2, retryCount - 1),
          MAX_DELAY_MS,
        );

        switch (error.status) {
          case 0:
          case 408:
          case 500:
          case 502:
          case 503:
          case 504:
            retryProgressService.notifyRetry(retryCount);
            return timer(delay);
          default:
            retryProgressService.reset();
            return throwError(() => error);
        }
      },
    }),
    catchError((error: HttpErrorResponse) => {
      retryProgressService.reset();
      return throwError(() => error);
    }),
    finalize(() => {
      retryProgressService.reset();
    }),
  );
};
